import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useProjects } from '../../hooks/useProjects'
import RoleBadge from '../layout/RoleBadge'
import api from '../../services/api'
import { isActiveProject } from '../../utils/helpers'
import { ProjectsByStatusPie, BudgetUtilizationBar, InvoiceQueueDonut } from '../common/DashboardCharts.jsx'
import QuotationView from '../finance/QuotationView.jsx'
import InvoiceDocumentView from '../finance/InvoiceDocumentView.jsx'
import AIActionGuide from '../ai/AIActionGuide.jsx'
import AgentProposals from './AgentProposals.jsx'
import { useTranslation } from 'react-i18next';

function FinanceDashboard() {
  const { t } = useTranslation('dashboards')
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate()
  const { user } = useAuth()
  const { projects } = useProjects()
  const [invoiceQueue, setInvoiceQueue] = useState([])
  const [paidInvoices, setPaidInvoices] = useState([])
  const [queueLoading, setQueueLoading] = useState(true)
  const [quotationProjectId, setQuotationProjectId] = useState(null)
  const [invoiceDocProjectId, setInvoiceDocProjectId] = useState(null)

  useEffect(() => {
    api.get('/deliverables/invoices/paid').then(r => setPaidInvoices(r.data || [])).catch(() => {})
    api.get('/deliverables/invoices/pending')
      .then(r => setInvoiceQueue(r.data))
      .catch(() => {})
      .finally(() => setQueueLoading(false))
  }, [])

  const projectList = Array.isArray(projects) ? projects : []

  // Derive stats from live project data. Use APPROVED budget (what Finance actually manages
  // and pays against) so these cards agree with the Budget Utilization chart, which is also
  // approved-budget based — total_budget includes un-approved projects the chart excludes.
  const approvedBudget = projectList.reduce((s, p) => s + (p.approved_budget || p.approvedBudget || 0), 0)
  const totalSpent = projectList.reduce((s, p) => s + (p.spent_budget || p.spentBudget || 0), 0)
  const remaining = Math.max(0, approvedBudget - totalSpent)
  const utilizationRate = approvedBudget > 0 ? ((totalSpent / approvedBudget) * 100).toFixed(1) : '0'

  const money = (n) => n >= 1000 ? `$${(n / 1000).toFixed(0)}K` : `$${(n || 0).toLocaleString()}`
  const pendingPaymentAmount = invoiceQueue.reduce((s, item) => s + (item.payment_amount || 0), 0)
  const paidValue = paidInvoices.reduce((s, item) => s + (item.payment_amount || 0), 0)

  const financeStats = [
    { name: t('approved_budget'), value: money(approvedBudget), icon: '💰' },
    { name: t('total_spent'), value: money(totalSpent), icon: '💸' },
    { name: t('pending_payments'), value: money(pendingPaymentAmount), icon: '⏳' },
    { name: 'Remaining', value: money(remaining), icon: '💵' },
  ]

  // Active means the same here as on the Admin and PM dashboards: open work, planned or started.
  // Counting only 'In Progress' left approved-but-not-yet-started projects out of the breakdown
  // while their budgets were still inside the totals above it, so the rows did not add up to
  // the cards.
  const activeProjects = projectList.filter(isActiveProject)
  const overdueInvoiceCount = invoiceQueue.length

  return (
    <div className="p-6 space-y-6">
      {/* AI Copilot action guide */}
      <AIActionGuide />
      {/* Prepared by the agent, not yet done — usually from an email. */}
      <AgentProposals />
      {/* Header */}
      <div className="bg-linear-to-r from-yellow-600 to-orange-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{t('finance_title')}</h1>
            <p className="mt-1 opacity-90">{t('finance_subtitle')}</p>
          </div>
          <div className="text-right">
            <p className="text-sm opacity-90">{t('budget_utilization')}</p>
            <p className="text-2xl font-bold">{utilizationRate}%</p>
            <RoleBadge role={user?.role} />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {financeStats.map((stat, index) => (
          <div key={index} className="card p-6 hover-lift">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{stat.value}</p>
              </div>
              <div className="text-2xl">{stat.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Dashboard Charts (Phase C2) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ProjectsByStatusPie projects={projects} />
        <BudgetUtilizationBar projects={projects} />
        <InvoiceQueueDonut
          paidCount={paidInvoices.length}
          pendingCount={invoiceQueue.length}
          paidValue={paidValue}
          pendingValue={pendingPaymentAmount}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Budget by Project */}
        <div className="lg:col-span-2 card">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{t('budget_breakdown')}</h3>
          </div>
          <div className="p-6">
            {activeProjects.length === 0 ? (
              <p className="text-center text-gray-500 py-8">{t('no_active_projects')}</p>
            ) : (
              <div className="space-y-4">
                {activeProjects.map((project, index) => {
                  const budget = project.total_budget || project.totalBudget || 0
                  const spent = project.spent_budget || project.spentBudget || 0
                  const pct = budget > 0 ? Math.min((spent / budget) * 100, 100) : 0
                  return (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                      <div className="flex items-center justify-between mb-3 gap-2">
                        <h4
                          onClick={() => navigate(`/projects/${project.id}`)}
                          className="font-medium text-gray-900 truncate cursor-pointer hover:text-blue-600 hover:underline"
                          title={t('open_project')}
                        >{project.name}</h4>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() => setQuotationProjectId(project.id)}
                            className="text-xs px-2 py-1 rounded-md border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                          >
                            🧾 Quote
                          </button>
                          <button
                            onClick={() => setInvoiceDocProjectId(project.id)}
                            className="text-xs px-2 py-1 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                          >
                            📄 Invoice
                          </button>
                          <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                            {project.status}
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                        <div>
                          <p className="text-gray-600">{t('allocated')}</p>
                          <p className="font-semibold">${budget.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">{t('spent')}</p>
                          <p className="font-semibold">${spent.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">{t('remaining')}</p>
                          <p className="font-semibold">${(budget - spent).toLocaleString()}</p>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm text-gray-600">{t('utilization')}</span>
                          <span className="text-sm font-medium text-gray-900">{pct.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${pct > 80 ? 'bg-red-600' : pct > 60 ? 'bg-yellow-600' : 'bg-green-600'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* Financial Alerts */}
        <div className="card">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{t('financial_health')}</h3>
          </div>
          <div className="p-6 space-y-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-3">{t('portfolio_overview')}</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('total_projects')}</span>
                  <span className="font-medium">{projectList.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{tc('status.active')}</span>
                  <span className="font-medium">{activeProjects.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{tc('status.completed')}</span>
                  <span className="font-medium">{projectList.filter(p => p.status === 'Completed' || p.status === 'Closed').length}</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 mb-3">{t('invoice_queue')}</h4>
              {queueLoading ? (
                <div className="animate-pulse h-8 bg-gray-100 rounded" />
              ) : (
                <div className="space-y-2">
                  {overdueInvoiceCount > 0 ? (
                    <>
                      <div className="flex items-center p-2 bg-amber-50 rounded-lg">
                        <div className="h-2 w-2 bg-amber-500 rounded-full mr-2 shrink-0" />
                        <span className="text-xs text-amber-800">
                          {overdueInvoiceCount} invoice{overdueInvoiceCount !== 1 ? 's' : ''} awaiting payment
                        </span>
                      </div>
                      <div className="flex items-center p-2 bg-amber-50 rounded-lg">
                        <div className="h-2 w-2 bg-amber-500 rounded-full mr-2 shrink-0" />
                        <span className="text-xs text-amber-800">
                          ${pendingPaymentAmount.toLocaleString()} pending release
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center p-2 bg-green-50 rounded-lg">
                      <div className="h-2 w-2 bg-green-500 rounded-full mr-2 shrink-0" />
                      <span className="text-xs text-green-800">{t('all_invoices_up_to_date')}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quotation modal */}
      {quotationProjectId && (
        <QuotationView projectId={quotationProjectId} onClose={() => setQuotationProjectId(null)} />
      )}

      {/* Per-project Invoice document modal */}
      {invoiceDocProjectId && (
        <InvoiceDocumentView projectId={invoiceDocProjectId} onClose={() => setInvoiceDocProjectId(null)} />
      )}
    </div>
  )
}

export default FinanceDashboard
